import { z } from 'zod';
import { DomainError } from '../errors.js';
import type { ParseResult, SchemaIssue } from '../voyage/schemas.js';

/**
 * Risk matrisi profili (ADR 0085 §2; ADR 0050 §2'yi değiştirir). 5×5 matris sabittir: skor =
 * olasılık × şiddet (1–25). Bantlar şirket ayarıdır: profilin değiştirilemez sürümü bantları ve
 * her bandın kabul seviyesini tutar. Gönderim kuralları ve seviye adı sürümden türer. Bantlar
 * 1–25'i boşluksuz ve çakışmasız kapsar; bant adı kapalı listedendir (i18n anahtarı ve renk).
 * Bant sınırları zorunlu mevzuat eşiği değildir, şirketin SMS kararıdır (FD §4, SS §3).
 */

/** Bant adları, düşükten yükseğe; ekranda `risk.levels.<ad>`. */
export const RA_BAND_KEYS = ['very_low', 'low', 'medium', 'high', 'very_high', 'critical'] as const;
export type RaBandKey = (typeof RA_BAND_KEYS)[number];

/**
 * Bandın kabul seviyesi, gevşekten sıkıya (FD §5 Tab 6): kabul edilebilir; ek önlem şart;
 * yükseltme (yalnız `acceptedBy` rolleri kabul eder); kabul edilemez (gönderim ve iş durur).
 */
export const RA_ACCEPTANCES = [
  'acceptable',
  'controls_required',
  'escalation',
  'unacceptable',
] as const;
export type RaAcceptance = (typeof RA_ACCEPTANCES)[number];

/** Kalan riski kabul eden roller (akış K3/K5'te; K1'de yalnız profil verisi). */
export const RA_ACCEPTORS = ['master', 'superintendent', 'dpa'] as const;
export type RaAcceptor = (typeof RA_ACCEPTORS)[number];

export const RA_MATRIX_MIN_BANDS = 2;
export const RA_MATRIX_MAX_BANDS = 6;
export const RA_SCORE_MAX = 25;

const score = z
  .number({ error: 'REQUIRED' })
  .int('INVALID')
  .min(1, 'OUT_OF_RANGE')
  .max(25, 'OUT_OF_RANGE');

const bandSchema = z.strictObject({
  key: z.enum(RA_BAND_KEYS, { error: 'INVALID' }),
  min: score,
  max: score,
  acceptance: z.enum(RA_ACCEPTANCES, { error: 'INVALID' }),
  acceptedBy: z
    .array(z.enum(RA_ACCEPTORS, { error: 'INVALID' }))
    .max(RA_ACCEPTORS.length, 'TOO_MANY'),
});
export type RaMatrixBand = z.output<typeof bandSchema>;

/** Sürümün tanımı: bantlar, düşükten yükseğe. */
export const raMatrixSchema = z.strictObject({
  bands: z
    .array(bandSchema)
    .min(RA_MATRIX_MIN_BANDS, 'TOO_FEW')
    .max(RA_MATRIX_MAX_BANDS, 'TOO_MANY'),
});
export type RaMatrixDefinition = z.output<typeof raMatrixSchema>;

/** Revizyon içeriğindeki anlık görüntü: sürüm kimliği, numarası ve tanımı (ADR 0085 §4). */
export interface RaMatrixSnapshot {
  versionId: string;
  version: number;
  definition: RaMatrixDefinition;
}

/**
 * Ortağın 4 bandı (FD §4, SS §3, XL 07 R-01…R-04); BUGA ve yeni şirketlerin varsayılanı (sahip
 * kararı 2026-10-19). High kalan riski kaptan ve ofiste DPA kabul eder; Critical işi durdurur.
 */
export const STANDARD_RISK_MATRIX: RaMatrixDefinition = {
  bands: [
    { key: 'low', min: 1, max: 4, acceptance: 'acceptable', acceptedBy: [] },
    { key: 'medium', min: 5, max: 9, acceptance: 'controls_required', acceptedBy: [] },
    { key: 'high', min: 10, max: 16, acceptance: 'escalation', acceptedBy: ['master', 'dpa'] },
    { key: 'critical', min: 17, max: 25, acceptance: 'unacceptable', acceptedBy: [] },
  ],
};

/**
 * Legacy 5-band (ADR 0050 §2, §3): matris sürümü taşımayan revizyonlar bununla okunur ve
 * denetlenir. Önlem Medium (≥ 6) ve üstünde şart; kalan risk Very High (≥ 16) kabul edilemez.
 */
export const LEGACY_RISK_MATRIX: RaMatrixDefinition = {
  bands: [
    { key: 'very_low', min: 1, max: 2, acceptance: 'acceptable', acceptedBy: [] },
    { key: 'low', min: 3, max: 5, acceptance: 'acceptable', acceptedBy: [] },
    { key: 'medium', min: 6, max: 9, acceptance: 'controls_required', acceptedBy: [] },
    { key: 'high', min: 10, max: 15, acceptance: 'controls_required', acceptedBy: [] },
    { key: 'very_high', min: 16, max: 25, acceptance: 'unacceptable', acceptedBy: [] },
  ],
};

export type RaMatrixIssueCode =
  | 'MATRIX_BOUNDS'
  | 'MATRIX_GAP'
  | 'MATRIX_OVERLAP'
  | 'MATRIX_EMPTY_BAND'
  | 'MATRIX_BAND_ORDER'
  | 'MATRIX_ACCEPTANCE_ORDER'
  | 'MATRIX_ACCEPTOR_REQUIRED'
  | 'MATRIX_ACCEPTOR_NOT_ALLOWED';

export interface RaMatrixIssue {
  code: RaMatrixIssueCode;
  /** Sorunlu bandın sırası (0'dan). */
  band: number;
}

/**
 * Tanımın kural sorunları (düzenleyicinin canlı önizlemesi ve sunucu doğrulaması aynı listeyi
 * kullanır). Sıra: bant içi, uçlar, komşu bantlar (boşluk, çakışma), ad sırası, kabul sırası,
 * kabul edenler.
 */
export function raMatrixIssues(definition: RaMatrixDefinition): RaMatrixIssue[] {
  const issues: RaMatrixIssue[] = [];
  const { bands } = definition;
  bands.forEach((band, i) => {
    if (band.min > band.max) issues.push({ code: 'MATRIX_EMPTY_BAND', band: i });
  });
  const first = bands[0];
  const last = bands.at(-1);
  if (first && first.min !== 1) issues.push({ code: 'MATRIX_BOUNDS', band: 0 });
  if (last && last.max !== RA_SCORE_MAX) {
    issues.push({ code: 'MATRIX_BOUNDS', band: bands.length - 1 });
  }
  for (let i = 1; i < bands.length; i += 1) {
    const previous = bands[i - 1] as RaMatrixBand;
    const band = bands[i] as RaMatrixBand;
    if (band.min > previous.max + 1) issues.push({ code: 'MATRIX_GAP', band: i });
    else if (band.min <= previous.max) issues.push({ code: 'MATRIX_OVERLAP', band: i });
    if (RA_BAND_KEYS.indexOf(band.key) <= RA_BAND_KEYS.indexOf(previous.key)) {
      issues.push({ code: 'MATRIX_BAND_ORDER', band: i });
    }
    if (RA_ACCEPTANCES.indexOf(band.acceptance) < RA_ACCEPTANCES.indexOf(previous.acceptance)) {
      issues.push({ code: 'MATRIX_ACCEPTANCE_ORDER', band: i });
    }
  }
  bands.forEach((band, i) => {
    if (band.acceptance === 'escalation' && band.acceptedBy.length === 0) {
      issues.push({ code: 'MATRIX_ACCEPTOR_REQUIRED', band: i });
    }
    if (band.acceptance === 'unacceptable' && band.acceptedBy.length > 0) {
      issues.push({ code: 'MATRIX_ACCEPTOR_NOT_ALLOWED', band: i });
    }
    if (new Set(band.acceptedBy).size !== band.acceptedBy.length) {
      issues.push({ code: 'MATRIX_ACCEPTOR_NOT_ALLOWED', band: i });
    }
  });
  return issues;
}

function toIssues(error: z.ZodError): SchemaIssue[] {
  return error.issues.map((issue) => ({
    path: issue.path.map(String).join('.'),
    code: issue.message,
  }));
}

/** Biçim denetimi (şema); kural denetimi `raMatrixIssues`. */
export function parseRaMatrix(input: unknown): ParseResult<RaMatrixDefinition> {
  const result = raMatrixSchema.safeParse(input);
  return result.success
    ? { ok: true, value: result.data }
    : { ok: false, issues: toIssues(result.error) };
}

/** Biçim hatası `INVALID_RISK_MATRIX`; kural sorununda ilk sorunun kodu (ayrıntı bant sırası). */
export function raMatrixOrThrow(input: unknown): RaMatrixDefinition {
  const parsed = parseRaMatrix(input);
  if (!parsed.ok) {
    throw new DomainError(
      'INVALID_RISK_MATRIX',
      parsed.issues.map((i) => `${i.path}:${i.code}`).join(','),
    );
  }
  const [issue] = raMatrixIssues(parsed.value);
  if (issue) throw new DomainError(issue.code, `band ${issue.band}`);
  return parsed.value;
}

export interface RiskRating {
  likelihood: number;
  severity: number;
  score: number;
  level: RaBandKey;
}

/** Skorun bandı; skor 1–25 tam sayı olmalı. */
export function bandOf(definition: RaMatrixDefinition, value: number): RaMatrixBand {
  if (!Number.isInteger(value) || value < 1 || value > RA_SCORE_MAX) {
    throw new DomainError('VALIDATION_FAILED', `score ${value}`, 'score');
  }
  const band = definition.bands.find((b) => value >= b.min && value <= b.max);
  if (!band) throw new DomainError('MATRIX_GAP', `score ${value}`);
  return band;
}

function scaleValue(value: number, field: string): number {
  if (!Number.isInteger(value) || value < 1 || value > 5) {
    throw new DomainError('VALIDATION_FAILED', `${field} ${value}`, field);
  }
  return value;
}

/** Olasılık ve şiddetin puanı ve matristeki seviyesi. */
export function ratingIn(
  definition: RaMatrixDefinition,
  likelihood: number,
  severity: number,
): RiskRating {
  const value = scaleValue(likelihood, 'likelihood') * scaleValue(severity, 'severity');
  return { likelihood, severity, score: value, level: bandOf(definition, value).key };
}

/** İki tanım aynı mı (anlık görüntünün sürüm satırıyla denetimi). */
export function sameRaMatrix(a: RaMatrixDefinition, b: RaMatrixDefinition): boolean {
  return JSON.stringify(raMatrixSchema.parse(a)) === JSON.stringify(raMatrixSchema.parse(b));
}
